import React, { createContext, useContext, useMemo } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInMilliseconds,
  format,
  getWeek,
  nextMonday,
  setDate,
  setDay,
  startOfDay,
  startOfWeek,
} from "date-fns";
import { ScheduleColumn } from "./ScheduleView";
import { sv } from "date-fns/locale";

export interface ScheduleEntry {
  id: React.Key;
  from: Date;
  to: Date;
}

export type ScheduleScale = "MONTHS" | "WEEKS" | "WORKWEEKS";

type ScheduleContextType<T extends ScheduleEntry> = {
  scale: ScheduleScale;
  entries: T[];
  columns: ScheduleColumn[];
  fromDate: Date;
  toDate: Date;
  totalDuration: number;
  enableSelection: boolean;
  selectedEntries: React.Key[];
  setSelectedEntries: (entryIds: React.Key[]) => void;
  toggleSelection: (entryId: React.Key) => void;
  toggleSelectionMany: (entryIds: React.Key[]) => void;
  showDayNumbers?: boolean;
};

type ScheduleProviderProps<T extends ScheduleEntry> = Omit<
  ScheduleContextType<T>,
  "columns" | "toggleSelection" | "toggleSelectionMany" | "totalDuration"
> &
  Partial<
    Pick<ScheduleContextType<T>, "enableSelection" | "selectedEntries" | "setSelectedEntries">
  > & {
    children: React.ReactNode;
  };

const ScheduleContext = createContext<ScheduleContextType<any> | undefined>(undefined);

const formatMonthYear = (date: Date) => format(date, "MMM yyyy", { locale: sv });
const formatWeek = (date: Date) => `V.${getWeek(date, { weekStartsOn: 1 })}`;

const useScheduleContext = () => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error("useScheduleContext must be used within a ScheduleProvider");
  }
  return context;
};

const ScheduleProvider: React.FC<ScheduleProviderProps<any>> = ({
  children,
  scale,
  fromDate: originalFromDate,
  toDate: originalToDate,
  entries,
  showDayNumbers,
  enableSelection,
  selectedEntries,
  setSelectedEntries,
}) => {
  // Align from/to dates to full weeks if needed based on scale
  const fromDate =
    scale === "WEEKS" || scale === "WORKWEEKS"
      ? setDay(originalFromDate, 1, { weekStartsOn: 1 })
      : originalFromDate;
  const toDate =
    scale === "WEEKS" || scale === "WORKWEEKS"
      ? nextMonday(originalToDate)
      : startOfDay(addDays(originalToDate, 1));

  const totalDuration = differenceInMilliseconds(toDate, fromDate);

  function toggleSelection(entryId: React.Key) {
    if (selectedEntries.includes(entryId)) {
      setSelectedEntries?.(selectedEntries.filter((id) => id !== entryId));
    } else {
      setSelectedEntries?.([...selectedEntries, entryId]);
    }
  }

  function toggleSelectionMany(entryIds: React.Key[]) {
    const isAnySelected = entryIds.some((id) => selectedEntries.includes(id));

    if (isAnySelected) {
      setSelectedEntries?.(selectedEntries.filter((id) => !entryIds.includes(id)));
    } else {
      setSelectedEntries?.([
        ...selectedEntries,
        ...entryIds.filter((id) => !selectedEntries.includes(id)),
      ]);
    }
  }

  // Calculate month/week segments for headers and column widths
  const columns = useMemo(() => {
    const columns: Array<ScheduleColumn> = [];

    function createColumn(startDate: Date, endDate: Date) {
      const columnDuration = differenceInMilliseconds(endDate, startDate);
      return {
        name: scale === "MONTHS" ? formatMonthYear(startDate) : formatWeek(startDate),
        ratio: (columnDuration / totalDuration) * 100,
        startDate: startDate,
      };
    }

    let segmentStart = fromDate;
    const segmentEnd =
      scale === "MONTHS"
        ? setDate(addMonths(segmentStart, 1), 1) // Start of next month
        : addWeeks(startOfWeek(segmentStart, { weekStartsOn: 1 }), 1); // Start of next week

    while (segmentEnd < toDate) {
      columns.push(createColumn(segmentStart, segmentEnd));

      segmentStart = new Date(segmentEnd);

      if (scale === "MONTHS") {
        segmentEnd.setMonth(segmentEnd.getMonth() + 1);
      } else {
        segmentEnd.setDate(segmentEnd.getDate() + 7);
      }
    }

    if (segmentStart < toDate) {
      columns.push(createColumn(segmentStart, toDate));
    }

    return columns;
  }, [fromDate, toDate, totalDuration, scale]);

  return (
    <ScheduleContext.Provider
      value={{
        scale: scale,
        enableSelection,
        selectedEntries,
        setSelectedEntries,
        toggleSelection,
        toggleSelectionMany,
        columns,
        fromDate,
        toDate,
        totalDuration,
        entries,
        showDayNumbers,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

export { ScheduleProvider as default, useScheduleContext };
